/** @file AI view panel: the renders and contact sheets AI clients made, newest first, per actor. */
import type { IDockviewPanelProps } from 'dockview-react';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { requestRenders } from '../../api/sync/requestRenders';
import { useT } from '../../shell/i18n/useT';
import { liveStore } from '../../stores/liveStore';
import { Button } from '../../ui-kit/Button';
import { Dialog } from '../../ui-kit/Dialog';
import '../activity/activity.css';
import './aiView.css';
import { aiViewStore } from './aiViewStore';
import { RenderCard } from './RenderCard';
import { renderCaption } from './renderCaption';

/** Actor chips (All first), the card grid, Load older, and the full-size dialog of the open entry. */
export function AiViewPanel(_props: IDockviewPanelProps) {
  const t = useT();
  const renders = useStore(liveStore, (s) => s.renders);
  const more = useStore(liveStore, (s) => s.rendersMore);
  const { actor, open } = useStore(aiViewStore, (s) => s);
  const actors = useMemo(() => [...new Set(renders.map((e) => e.actor))].sort(), [renders]);
  const shown = useMemo(() => renders.filter((e) => actor === null || e.actor === actor).reverse(), [renders, actor]);
  const opened = renders.find((e) => e.seq === open) ?? null;
  const pick = (next: string | null): void => { aiViewStore.setState({ actor: next }); };
  return (
    <div className="ai-view" data-testid="panel-aiView">
      <div className="ai-view-actors" role="group" aria-label={t('aiView.actors')}>
        <Button label={t('aiView.allActors')} primary={actor === null} onClick={() => { pick(null); }} />
        {actors.map((name) => <Button key={name} label={name} primary={actor === name} onClick={() => { pick(name); }} />)}
      </div>
      {shown.length === 0 ? <p className="ai-view-empty">{t('aiView.empty')}</p> : null}
      <ul className="ai-view-grid">{shown.map((e) => <RenderCard key={e.seq} entry={e} onOpen={() => { aiViewStore.setState({ open: e.seq }); }} />)}</ul>
      {more && renders.length > 0 ? <Button label={t('aiView.loadOlder')} onClick={() => { requestRenders(renders[0]?.seq ?? null); }} /> : null}
      <Dialog open={opened !== null} title={opened === null ? '' : renderCaption(t, opened)} closeLabel={t('aiView.close')} onClose={() => { aiViewStore.setState({ open: null }); }}>
        {opened === null ? null : <img className="ai-view-full" src={opened.url} alt={renderCaption(t, opened)} />}
      </Dialog>
    </div>
  );
}
